import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { LuCircleCheck, LuCircleX, LuClock, LuFileText, LuListChecks, LuPlus } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import Button from '../../../components/ui/Button';
import SegmentedControl from '../../../components/ui/SegmentedControl';
import { useTranslation } from '../../../i18n/LanguageContext';
import { CARD, DetailItem, EmptyState, LoadingRows, SectionHeader, SimplePager } from '../components/ProfileUi';
import { LEAVE_STATUS_TONES, leaveTypeLabel } from './leaveOptions';

const PAGE_SIZE = 5;
const STATUS_ICONS = { PENDING: LuClock, APPROVED: LuCircleCheck, REJECTED: LuCircleX };

const toDate = (value) => (value?.toDate ? value.toDate() : new Date(value));

/**
 * "İzin Taleplerim": every leave request with its status, the manager's note
 * and who decided; leaves an admin entered directly are listed too.
 */
const LeaveRequestList = ({ requests, loading, onNewRequest }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.leave.list.${key}`, vars);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);

  const formatDay = (value) => {
    if (!value) return '—';
    const date = toDate(value);
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleDateString(locale, { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const counts = useMemo(() => ({
    all: requests.length,
    PENDING: requests.filter((r) => r.status === 'PENDING').length,
    APPROVED: requests.filter((r) => r.status === 'APPROVED').length,
    REJECTED: requests.filter((r) => r.status === 'REJECTED').length,
  }), [requests]);

  const filtered = filter === 'all' ? requests : requests.filter((request) => request.status === filter);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const current = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  let body;
  if (loading) {
    body = <LoadingRows count={3} height="tw-h-28" />;
  } else if (requests.length === 0) {
    body = (
      <EmptyState
        icon={LuListChecks}
        title={label('empty')}
        text={label('emptyHint')}
        action={<Button variant="primary" size="sm" onClick={onNewRequest}><LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />{label('newRequest')}</Button>}
      />
    );
  } else if (filtered.length === 0) {
    body = <EmptyState compact icon={LuListChecks} title={label('noMatch')} />;
  } else {
    body = (
      <ul className="tw-space-y-3">
        {current.map((request) => {
          const StatusIcon = STATUS_ICONS[request.status] || LuClock;
          const sentOn = request.createdAt ? toDate(request.createdAt) : null;
          let noteTone = 'tw-bg-slate-500/[0.07] tw-text-slate-700 dark:tw-text-slate-200';
          if (request.status === 'APPROVED') noteTone = 'tw-bg-emerald-500/10 tw-text-emerald-900 dark:tw-text-emerald-100';
          if (request.status === 'REJECTED') noteTone = 'tw-bg-rose-500/10 tw-text-rose-900 dark:tw-text-rose-100';
          return (
            <li key={request.id} className={`${CARD} tw-space-y-3 tw-p-4`}>
              <div className="tw-flex tw-flex-wrap tw-items-start tw-justify-between tw-gap-2">
                <div className="tw-min-w-0">
                  <p className="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{leaveTypeLabel(t, request.leaveType)}</p>
                  <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    {sentOn && !Number.isNaN(sentOn.getTime()) ? label('sentOn', { date: sentOn.toLocaleDateString(locale) }) : label('unknownDate')}
                    {' · '}
                    {request.source === 'LeaveRequests' ? label('sourceRequest') : label('sourceAdmin')}
                  </p>
                </div>
                {LEAVE_STATUS_TONES[request.status] && (
                  <Badge tone={LEAVE_STATUS_TONES[request.status]}>
                    <StatusIcon aria-hidden="true" className="tw-h-3 tw-w-3" />
                    {t(`profilePage.leave.status.${request.status}`)}
                  </Badge>
                )}
              </div>

              <dl className="tw-grid tw-grid-cols-2 tw-gap-3 sm:tw-grid-cols-4">
                <DetailItem label={label('start')}>{formatDay(request.startDate)}</DetailItem>
                <DetailItem label={label('end')}>{formatDay(request.endDate)}</DetailItem>
                <DetailItem label={label('days')}>{label('dayCount', { count: Number(request.leaveDays) || 0 })}</DetailItem>
                <DetailItem label={label('travelDays')}>{label('dayCount', { count: Number(request.travelDays) || 0 })}</DetailItem>
                {request.reason && <DetailItem label={label('reason')} wide>{request.reason}</DetailItem>}
                {request.address && <DetailItem label={label('address')} wide>{request.address}</DetailItem>}
              </dl>

              {request.reportFile?.url && (
                <a
                  href={request.reportFile.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-sm tw-font-semibold tw-text-teal-700 hover:tw-underline dark:tw-text-teal-300"
                >
                  <LuFileText aria-hidden="true" className="tw-h-4 tw-w-4" />
                  {request.reportFile.name || label('report')}
                </a>
              )}

              {request.adminNote && (
                <div className={`tw-rounded-xl tw-px-3.5 tw-py-2.5 tw-text-sm ${noteTone}`}>
                  <p className="tw-text-[11px] tw-font-semibold tw-uppercase tw-tracking-wide tw-opacity-70">{label('adminNote')}</p>
                  <p className="tw-mt-0.5 tw-whitespace-pre-line">{request.source === 'EmployeeLeaves' ? label('createdByAdmin') : request.adminNote}</p>
                </div>
              )}

              {request.processedBy && (
                <p className="tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                  {request.status === 'PENDING' ? label('notReviewed') : label('reviewedBy', { name: request.processedBy })}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className="tw-space-y-4">
      <SectionHeader
        icon={LuListChecks}
        gradient="tw-from-sky-400 tw-to-blue-600 tw-shadow-sky-500/30"
        title={label('title')}
        description={label('description')}
      >
        <Button size="sm" onClick={onNewRequest}>
          <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
          {label('newRequest')}
        </Button>
      </SectionHeader>

      {!loading && requests.length > 0 && (
        <div className="tw-overflow-x-auto tw-pb-1">
          <SegmentedControl
            size="sm"
            layoutId="profile-leave-filter"
            label={label('filterLabel')}
            value={filter}
            onChange={setFilter}
            options={['all', 'PENDING', 'APPROVED', 'REJECTED'].map((key) => ({
              value: key,
              label: `${key === 'all' ? label('all') : t(`profilePage.leave.status.${key}`)} (${counts[key]})`,
            }))}
          />
        </div>
      )}

      {body}

      {!loading && <SimplePager page={page} pageCount={pageCount} onChange={setPage} />}
    </div>
  );
};

LeaveRequestList.propTypes = {
  requests: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  onNewRequest: PropTypes.func.isRequired,
};

export default LeaveRequestList;
